import { getNavigatorGeometryFault, getNavigatorState } from "./navigatorStateHelper";

/**
 * Canonical display states for a machine in the UI.
 *
 * - "off"     → autonomy is not running (VADC state 0, 3, 255, or undefined)
 * - "ready"   → autonomy on, not actively running a mission (VADC state 1 / STANDBY)
 * - "paused"  → autonomy active but navigator interrupted (VADC state 2 + Navigator INTERRUPTED)
 * - "working" → autonomy active and navigator not interrupted (VADC state 2 + other)
 *
 */
export type MachineDisplayState = "off" | "ready" | "paused" | "working";

export interface MachineDisplayInfo {
  /** Canonical display state derived from VADC + Navigator */
  displayState: MachineDisplayState;
  /**  label shown next to the icon (e.g. "Off", "Ready", "Paused", "Working") */
  label: string;
  /** CSS color string for the label text and icon tint */
  color: string;
  /** Raw VADC state as a clean number */
  vadcState: number;
  /** Raw navigator state string, or undefined if unavailable */
  navigatorState: string | undefined;
}

const STATE_INFO: Record<MachineDisplayState, { label: string; color: string }> = {
  off: { label: "Off", color: "#000000" },
  ready: { label: "Ready", color: "#00B600" },
  paused: { label: "Paused", color: "rgb(255, 120, 0)" },
  working: { label: "Working", color: "#00B600" },
};

/** Speed above which the machine is clearly moving, in m/s. */
const MOVING_SPEED_MPS = 0.1;

/**
 * True when the navigator reports unusable jobsite geometry AND the machine is
 * stationary. The speed check matters because the org snapshot keeps the last
 * DebugString until the machine publishes a new one, so a stale fault would
 * otherwise show a machine that is actively driving as not working.
 */
export function isStalledByGeometryFault(machineData: any): boolean {
  if (!getNavigatorGeometryFault(machineData?.Navigator)) return false;
  const speed = Number(
    machineData?.MachineState?.GlobalPose?.speed ?? machineData?.MachineState?.Pose?.speed,
  );
  return !Number.isFinite(speed) || Math.abs(speed) <= MOVING_SPEED_MPS;
}

/**
 * Derive the canonical display state for a machine from its live data.
 *
 * @param machineData The data object containing `VADC` and `Navigator`
 *
 */
export function getMachineDisplayState(machineData: any): MachineDisplayInfo {
  const vadcState = Number(machineData?.VADC?.autonomy?.state?.[0]);
  const navigatorState = getNavigatorState(machineData?.Navigator?.NavigatorState);

  let displayState: MachineDisplayState;
  if (vadcState !== 1 && vadcState !== 2) {
    displayState = "off";
  } else if (vadcState === 1) {
    displayState = "ready";
  } else if (vadcState === 2 && navigatorState === "INTERRUPTED") {
    displayState = "paused";
  } else if (vadcState === 2 && isStalledByGeometryFault(machineData)) {
    // Autonomy is engaged but the navigator cannot plan against the jobsite
    // geometry, so the machine is not working despite VADC reporting ACTIVE.
    displayState = "ready";
  } else {
    displayState = "working";
  }

  const { label, color } = STATE_INFO[displayState];
  return { displayState, label, color, vadcState, navigatorState };
}
